import { BookOpen, Zap, Compass, ShieldCheck } from "lucide-react";

const gettingStarted = [
  {
    slug: "introduction",
    section: "getting-started",
    title: "Introduction to Acadence",
    subtitle:
      "A unified Education ERP platform built for colleges and universities — 21 modules, 6 roles, one system.",
    icon: BookOpen,
    readTime: "5 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Welcome to Acadence",
        body: "Acadence is a fully integrated Education Management System (ERP) designed to digitise and streamline every operation of a higher-education institution — from admissions to alumni management.",
      },
      {
        type: "heading",
        level: 2,
        text: "What is Acadence?",
      },
      {
        type: "text",
        body: "Acadence brings together 21 functional modules under one platform, replacing disconnected spreadsheets, standalone portals, and manual processes. It is purpose-built for colleges, universities, and vocational institutes that need a single source of truth for academic data, student records, and administrative workflows.",
      },
      {
        type: "text",
        body: "Every module shares the same database, authentication layer, and notification system — meaning data entered in Admissions is immediately visible in Fees, Attendance, and Reports without any manual sync.",
      },
      {
        type: "heading",
        level: 2,
        text: "Platform Domains",
      },
      {
        type: "text",
        body: "Acadence is organised into four functional domains, each covering a group of related modules:",
      },
      {
        type: "table",
        columns: ["Domain", "Modules Included", "Primary Users"],
        rows: [
          [
            "Student Lifecycle",
            "Admissions, Enrollment, ID Cards, Certificates, Alumni",
            "Admin, Student",
          ],
          [
            "Academics",
            "Attendance, Grades, Timetable, Assignments, Library, Examination",
            "Faculty, Student, Admin",
          ],
          [
            "Administration",
            "Fees, HR, Payroll, Inventory, Complaints, Reports",
            "Admin, HR Manager",
          ],
          [
            "Engagement",
            "Notifications, Placements, WhatsApp Integration",
            "All Roles",
          ],
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Who Uses Acadence?",
      },
      {
        type: "text",
        body: "Acadence serves six distinct user roles, each with a tailored dashboard and access controls:",
      },
      {
        type: "list",
        style: "unordered",
        items: [
          "Administrators — full system access; manage users, configuration, and all modules",
          "Students — view timetables, grades, assignments, fees, library, and placement details",
          "Faculty — manage attendance, post assignments, upload grades, and view student profiles",
          "Parents — monitor their child's attendance, grades, fee status, and notifications",
          "HR Managers — manage staff records, payroll, leave, and recruitment",
          "Placement Officers — manage placement drives, company visits, and student placement status",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Key Capabilities",
      },
      {
        type: "steps",
        items: [
          {
            title: "Unified Authentication",
            description:
              "Single sign-on for all roles with cookie-based JWT sessions. No separate portals or passwords per module.",
          },
          {
            title: "Real-Time Data",
            description:
              "All modules read and write to a shared MongoDB database — attendance marked by faculty appears instantly in parent and admin views.",
          },
          {
            title: "Role-Based Access Control",
            description:
              "Each role has a precisely scoped permission set. Students cannot access HR data; parents see only their enrolled child's records.",
          },
          {
            title: "WhatsApp Notifications",
            description:
              "Built-in WhatsApp integration sends automated alerts for fee reminders, attendance shortfall, exam schedules, and placement updates.",
          },
          {
            title: "Comprehensive Reporting",
            description:
              "Cross-module reports give administrators a 360-degree view — enrollment trends, fee collection, attendance rates, and placement statistics.",
          },
        ],
      },
      {
        type: "callout",
        variant: "success",
        title: "Ready to get started?",
        body: "Head to the Quick Start guide to log in for the first time and explore your role-specific dashboard.",
      },
    ],
  },
  {
    slug: "quick-start",
    section: "getting-started",
    title: "Quick Start",
    subtitle:
      "Log in, explore your dashboard, and complete your first key task in under 10 minutes.",
    icon: Zap,
    readTime: "4 min read",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Prerequisites",
        body: "You need valid credentials from your institution's administrator. If you are an admin setting up Acadence for the first time, see the self-hosting or cloud setup guide.",
      },
      {
        type: "heading",
        level: 2,
        text: "Step 1 — Log In",
      },
      {
        type: "steps",
        items: [
          {
            title: "Open the Acadence URL",
            description:
              "Navigate to your institution's Acadence URL in a modern browser (Chrome, Firefox, Edge, or Safari).",
          },
          {
            title: "Enter your credentials",
            description:
              "Type your institutional email address and password. These are provided by your system administrator.",
          },
          {
            title: "Click Sign In",
            description:
              "Acadence authenticates your credentials and automatically redirects you to the dashboard for your role.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Dashboard Overview by Role",
      },
      {
        type: "text",
        body: "After login, you land on a role-specific dashboard. Here is a summary of what each role sees at a glance:",
      },
      {
        type: "table",
        columns: ["Role", "Dashboard Highlights", "Quick Actions"],
        rows: [
          [
            "Admin",
            "Total students, fee collection, attendance rate, recent admissions",
            "Add student, generate report, send notification",
          ],
          [
            "Student",
            "Today's timetable, attendance %, pending fees, recent grades",
            "View assignments, check exam schedule, download ID card",
          ],
          [
            "Faculty",
            "Today's classes, pending attendance, assignment submissions due",
            "Mark attendance, upload grades, post assignment",
          ],
          [
            "Parent",
            "Child's attendance, latest grades, fee due date, notifications",
            "Pay fees, send complaint, view timetable",
          ],
          [
            "HR Manager",
            "Staff headcount, pending leave requests, payroll status",
            "Process payroll, approve leave, add staff",
          ],
          [
            "Placement Officer",
            "Active drives, students placed, upcoming company visits",
            "Create drive, update placement status, send alerts",
          ],
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "First Steps by Role",
      },
      {
        type: "callout",
        variant: "tip",
        title: "Admin — First Steps",
        body: "1. Go to Settings and verify your institution's name, logo, and academic year. 2. Navigate to Users and confirm all faculty and student accounts are active. 3. Check the Reports module for an enrollment and fee collection summary.",
      },
      {
        type: "callout",
        variant: "tip",
        title: "Student — First Steps",
        body: "1. Open Timetable to view your class schedule for today and the week. 2. Check Attendance to verify your percentage and any shortfall warnings. 3. Visit Fees to see your outstanding dues and payment history.",
      },
      {
        type: "callout",
        variant: "tip",
        title: "Faculty — First Steps",
        body: "1. Open Attendance and mark attendance for your first class of the day. 2. Go to Assignments and post your first assignment with a due date. 3. Check Grades to review any pending grade entries for your subjects.",
      },
      {
        type: "heading",
        level: 2,
        text: "Navigating Between Modules",
      },
      {
        type: "callout",
        variant: "info",
        title: "Tip",
        body: "Use the sidebar on the left to switch between modules instantly. The sidebar is grouped by domain (Academics, Administration, etc.) and shows only the modules your role can access. You can collapse sections you don't use frequently.",
      },
    ],
  },
  {
    slug: "navigation",
    section: "getting-started",
    title: "Navigating the Platform",
    subtitle:
      "Understand the layout of the Acadence interface — sidebar, main area, top bar, and mobile navigation.",
    icon: Compass,
    readTime: "4 min read",
    content: [
      {
        type: "heading",
        level: 2,
        text: "Dashboard Layout",
      },
      {
        type: "text",
        body: "The Acadence interface is divided into three persistent regions. Understanding these regions will help you work faster and find information efficiently.",
      },
      {
        type: "list",
        style: "unordered",
        items: [
          "Sidebar (left) — module navigation grouped by domain, with collapse/expand support",
          "Main content area (center) — the active module's page; changes as you navigate",
          "Top bar (top) — institution name, search, notification bell, and your profile avatar",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Using the Sidebar",
      },
      {
        type: "steps",
        items: [
          {
            title: "Open a module",
            description:
              "Click any item in the sidebar to navigate directly to that module. The active item is highlighted.",
          },
          {
            title: "Collapse a section",
            description:
              "Click the section heading (e.g., 'Academics') to collapse or expand that group of links, keeping the sidebar uncluttered.",
          },
          {
            title: "Collapse the entire sidebar",
            description:
              "Click the toggle icon at the top of the sidebar to collapse it to icon-only mode, giving more space to the main content area.",
          },
          {
            title: "Return to dashboard",
            description:
              "Click the Acadence logo or the 'Dashboard' link at the top of the sidebar to return to your role-specific home screen.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Top Bar",
      },
      {
        type: "text",
        body: "The top bar provides quick access to global features available on every page:",
      },
      {
        type: "list",
        style: "unordered",
        items: [
          "Search — type any student name, module name, or record ID to jump directly to it",
          "Notifications — the bell icon shows unread alerts; click to open the notification panel",
          "Profile avatar — click to open a dropdown with 'Edit Profile', 'Change Password', and 'Sign Out'",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Mobile Navigation",
      },
      {
        type: "callout",
        variant: "info",
        title: "Responsive Design",
        body: "Acadence is fully responsive. On screens smaller than 768 px, the sidebar collapses into a hamburger menu. Tap the menu icon in the top-left corner to open the navigation drawer. All modules are accessible on mobile.",
      },
      {
        type: "text",
        body: "Touch-friendly elements throughout the interface make common tasks — marking attendance, viewing timetables, checking fee status — easy to complete on a phone or tablet.",
      },
      {
        type: "heading",
        level: 2,
        text: "Profile & Settings",
      },
      {
        type: "steps",
        items: [
          {
            title: "Edit your profile",
            description:
              "Click your avatar in the top bar and select 'Edit Profile'. You can update your display name, contact number, and profile photo.",
          },
          {
            title: "Change your password",
            description:
              "Go to 'Change Password' from the profile dropdown. You must enter your current password before setting a new one.",
          },
          {
            title: "System settings (Admin only)",
            description:
              "Admins can access the Settings module from the sidebar to configure institution details, academic year, notification templates, and user management.",
          },
        ],
      },
      {
        type: "callout",
        variant: "warning",
        title: "Session Timeout",
        body: "For security, sessions expire after 24 hours of inactivity. If you see a login prompt unexpectedly, your session has expired — log in again to continue.",
      },
    ],
  },
  {
    slug: "roles-and-permissions",
    section: "getting-started",
    title: "Roles & Permissions",
    subtitle:
      "Every Acadence user has a role that determines which modules they can access and what actions they can perform.",
    icon: ShieldCheck,
    readTime: "6 min read",
    content: [
      {
        type: "heading",
        level: 2,
        text: "The Six Roles",
      },
      {
        type: "text",
        body: "Acadence uses role-based access control (RBAC). A user's role is assigned by an administrator at account creation and can be changed at any time. Each role has a dedicated dashboard and a scoped permission set.",
      },
      {
        type: "role-badge",
        roles: [
          {
            role: "admin",
            label: "Admin",
            description:
              "Full access to all 21 modules. Creates and manages all user accounts, configures institution settings, and generates cross-module reports.",
          },
          {
            role: "student",
            label: "Student",
            description:
              "Read-only access to personal academic records — timetable, attendance, grades, assignments, fees, library, and placements.",
          },
          {
            role: "faculty",
            label: "Faculty",
            description:
              "Manages academic data for their assigned subjects and courses — marks attendance, posts assignments, uploads grades, and views student profiles.",
          },
          {
            role: "parent",
            label: "Parent",
            description:
              "View-only access to their linked child's records — attendance, grades, fees, and notifications. Can submit complaints.",
          },
          {
            role: "hr_manager",
            label: "HR Manager",
            description:
              "Manages all staff-related modules — HR records, payroll, leave management, and recruitment. No access to student academic data.",
          },
          {
            role: "placement_officer",
            label: "Placement Officer",
            description:
              "Manages placement drives, company visit schedules, and student placement status. Can send targeted placement notifications.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Permission Matrix",
      },
      {
        type: "text",
        body: "The table below summarises access levels across all 21 modules. Full = create, read, update, delete. View = read-only. Own = access to own records only.",
      },
      {
        type: "table",
        columns: [
          "Module",
          "Admin",
          "Student",
          "Faculty",
          "Parent",
          "HR Manager",
          "Placement Officer",
        ],
        rows: [
          ["Admissions", "Full", "—", "—", "—", "—", "—"],
          ["Enrollment", "Full", "View (own)", "View", "View (child)", "—", "—"],
          ["Attendance", "Full", "View (own)", "Full", "View (child)", "—", "—"],
          ["Grades", "Full", "View (own)", "Full", "View (child)", "—", "—"],
          ["Timetable", "Full", "View", "View", "View (child)", "—", "—"],
          ["Assignments", "Full", "View & Submit", "Full", "View (child)", "—", "—"],
          ["Fees", "Full", "View (own)", "—", "View (child)", "—", "—"],
          ["Examinations", "Full", "View (own)", "Full", "View (child)", "—", "—"],
          ["Library", "Full", "View & Borrow", "View", "—", "—", "—"],
          ["ID Cards", "Full", "View (own)", "—", "—", "—", "—"],
          ["Certificates", "Full", "View (own)", "—", "—", "—", "—"],
          ["Alumni", "Full", "View (own)", "—", "—", "—", "—"],
          ["HR", "View", "—", "—", "—", "Full", "—"],
          ["Payroll", "View", "—", "—", "—", "Full", "—"],
          ["Inventory", "Full", "—", "—", "—", "View", "—"],
          ["Complaints", "Full", "Submit", "Submit", "Submit", "Submit", "Submit"],
          ["Placements", "Full", "View (own)", "—", "—", "—", "Full"],
          ["Notifications", "Full", "View (own)", "Send (class)", "View (own)", "View (own)", "Send"],
          ["Reports", "Full", "—", "View (own)", "—", "HR Reports", "Placement Reports"],
          ["Settings", "Full", "—", "—", "—", "—", "—"],
          ["WhatsApp", "Full", "—", "—", "—", "—", "—"],
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Role Details",
      },
      {
        type: "callout",
        variant: "info",
        title: "Admin",
        body: "Admins are the system superusers. They can create, edit, and deactivate any account, configure every module, generate all reports, and manage institution-level settings. There must be at least one active admin account at all times.",
      },
      {
        type: "callout",
        variant: "info",
        title: "Faculty",
        body: "Faculty members are assigned to specific subjects and batches. They can only mark attendance and upload grades for their assigned subjects. An admin must first map a faculty member to a subject before these actions appear in their dashboard.",
      },
      {
        type: "callout",
        variant: "warning",
        title: "Parent Access",
        body: "A parent account must be explicitly linked to a student by an admin. Until linked, a parent sees an empty dashboard. One parent account can be linked to multiple students (e.g., siblings).",
      },
      {
        type: "callout",
        variant: "info",
        title: "HR Manager",
        body: "The HR Manager role is completely siloed from student academic data. They have full control over the HR, Payroll, and Leave modules but cannot access Admissions, Grades, or other student-facing modules.",
      },
    ],
  },
];

export default gettingStarted;
